import {
    GROUP_ROW_ID_PREFIX,
    GroupByRow,
    GroupChildRow,
    GroupChildrenState,
    GroupDescriptor,
    GroupHeaderRow,
    itemRowId,
} from "./groupByRowTypes";

interface BuildGroupedTreeParams<TItem> {
    groups: GroupDescriptor[];
    childrenByKey: Record<string, GroupChildrenState<TItem> | undefined>;
    getItemRowId: (item: TItem) => string;
    skeletonRowCount?: number;
    /** Caps the load-more row's count at one page (what a click fetches). */
    loadMorePageSize?: number;
}

const DEFAULT_SKELETON_ROW_COUNT = 3;

/**
 * Pure: turns group descriptors + per-group loading state into the nesting tree
 * ViewTable renders. Unloaded groups get skeleton placeholder children so the
 * group is expandable at all (TanStack only expands rows that already have
 * sub-rows) — the skeletons stay hidden until the group is expanded, which is
 * what triggers the group's first fetch.
 */
export function buildGroupedTree<TItem>({
    groups,
    childrenByKey,
    getItemRowId,
    skeletonRowCount = DEFAULT_SKELETON_ROW_COUNT,
    loadMorePageSize,
}: BuildGroupedTreeParams<TItem>): Array<GroupHeaderRow<TItem>> {
    return groups.map((descriptor) => ({
        id: `${GROUP_ROW_ID_PREFIX}${descriptor.key}`,
        rowType: "groupHeader",
        descriptor,
        children: buildGroupChildren({
            descriptor,
            state: childrenByKey[descriptor.key],
            getItemRowId,
            skeletonRowCount,
            loadMorePageSize,
        }),
    }));
}

interface BuildGroupChildrenParams<TItem> {
    descriptor: GroupDescriptor;
    state: GroupChildrenState<TItem> | undefined;
    getItemRowId: (item: TItem) => string;
    skeletonRowCount: number;
    loadMorePageSize?: number;
}

function buildGroupChildren<TItem>({
    descriptor,
    state,
    getItemRowId,
    skeletonRowCount,
    loadMorePageSize,
}: BuildGroupChildrenParams<TItem>): Array<GroupChildRow<TItem>> {
    if (descriptor.totalCount === 0) {
        return [];
    }

    // Not loaded yet, first page in flight, or an empty unsettled state (the
    // render tick between a group's query being enabled and react-query
    // flipping isLoading) → skeletons (hidden until expand), never more than
    // the group actually holds. A SETTLED empty group (stale totalCount)
    // falls through and renders no children.
    if (
        !state ||
        state.isLoadingFirstPage ||
        (state.items.length === 0 && !state.isError && !state.isSettled)
    ) {
        return skeletonRows<TItem>(
            descriptor.key,
            Math.min(skeletonRowCount, descriptor.totalCount),
        );
    }

    // First-page fetch failed with nothing loaded → recoverable error row.
    if (state.isError && state.items.length === 0) {
        return [loadErrorRow<TItem>(descriptor.key)];
    }

    const rows: Array<GroupChildRow<TItem>> = state.items.map((item) => ({
        id: itemRowId(getItemRowId(item)),
        rowType: "item",
        item,
    }));

    if (state.isLoadingMore) {
        const remainingCount = Math.max(
            descriptor.totalCount - state.items.length,
            0,
        );
        rows.push(
            ...skeletonRows<TItem>(
                descriptor.key,
                Math.min(skeletonRowCount, remainingCount),
            ),
        );
    } else if (state.isError) {
        // Load-more failed but keep the rows already loaded; offer retry.
        rows.push(loadErrorRow<TItem>(descriptor.key));
    } else if (state.hasMore) {
        const remainingCount = Math.max(
            descriptor.totalCount - state.items.length,
            0,
        );
        // Stale totalCount can make this 0 while hasMore is true — skip the
        // row rather than render "Load 0 more".
        if (remainingCount > 0) {
            rows.push({
                id: `loadMore:${descriptor.key}`,
                rowType: "loadMore",
                groupKey: descriptor.key,
                isLoadingMore: false,
                remainingCount:
                    loadMorePageSize === undefined
                        ? remainingCount
                        : Math.min(remainingCount, loadMorePageSize),
            });
        }
    }

    return rows;
}

function skeletonRows<TItem>(
    groupKey: string,
    count: number,
): Array<GroupChildRow<TItem>> {
    return Array.from({ length: count }, (_unused, index) => ({
        id: `skeleton:${groupKey}:${index}`,
        rowType: "skeleton" as const,
        groupKey,
    }));
}

function loadErrorRow<TItem>(groupKey: string): GroupChildRow<TItem> {
    return {
        id: `loadError:${groupKey}`,
        rowType: "loadError",
        groupKey,
    };
}

export const getGroupedRowId = <TItem>(row: GroupByRow<TItem>): string =>
    row.id;
